/**
 * Service d'authentification
 * Gère la connexion, la déconnexion et la vérification de l'état d'authentification
 */

import { BaseUser, LoginResponse as SharedLoginResponse } from '../..\types\auth';

// Type pour la réponse de l'API de connexion
interface LoginResponse extends SharedLoginResponse {
  user?: BaseUser;
  [key: string]: any; // Pour les champs supplémentaires
}

// Clé pour stocker le token dans le localStorage
export const TOKEN_KEY = 'authToken'; // Modifié pour correspondre à la clé utilisée par Xano
const USER_KEY = 'auth_user';

/**
 * Vérifie si l'utilisateur est authentifié
 * @returns boolean - true si authentifié, false sinon
 */
export function isAuthenticated(): boolean {
  return !!localStorage.getItem(TOKEN_KEY);
}

/**
 * Récupère le token d'authentification
 * @returns string | null - Le token ou null si non trouvé
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Stocke le token d'authentification
 * @param token - Le token à stocker
 */
function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Stocke les informations de l'utilisateur
 * @param user - Les données de l'utilisateur
 */
function setUser(user: BaseUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * Récupère les informations de l'utilisateur
 * @returns BaseUser | null - Les données de l'utilisateur ou null si non trouvées
 */
export function getUser(): BaseUser | null {
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
}

/**
 * Effectue une requête de connexion
 * @param credentials - Les identifiants de connexion (email et mot de passe)
 * @returns Promise<LoginResponse> - Les données de l'utilisateur et le token
 */
// Type pour les identifiants de connexion
interface LoginCredentials {
  email: string;
  password: string;
}

export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  // Utilisation de la configuration de l'API Xano
  const API_BASE_URL = 'https://xpaf-zlrl-h7lr.f2.xano.io';
  const LOGIN_ENDPOINT = 'api:eUb8OK3m:dev/auth/login';

  try {
    const response = await fetch(`${API_BASE_URL}/${LOGIN_ENDPOINT}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    let responseData;
    const contentType = response.headers.get('content-type');

    try {
      // Essayer de parser la réponse en JSON
      responseData = await response.json();
    } catch (jsonError) {
      const textResponse = await response.text();
      throw new Error(`Réponse inattendue du serveur: ${response.status} ${response.statusText}`);
    }

    if (!response.ok) {
      throw new Error(responseData.message || `Erreur ${response.status}: ${response.statusText}`);
    }

    // Vérifions que la réponse contient bien un token
    // Le serveur renvoie le token dans le champ 'authToken' et non 'token'
    const authToken = responseData.authToken || responseData.token;

    if (!authToken) {
      throw new Error("La réponse du serveur ne contient pas de token d'authentification");
    }

    // Stocker le token
    try {
      localStorage.setItem(TOKEN_KEY, authToken);
    } catch (storageError) {
      throw new Error("Unable to store authentication token");
    }

    // Stocker les données utilisateur si elles sont présentes
    if (responseData.user) {
      setUser(responseData.user);
    }

    return responseData;
  } catch (error) {
    throw error;
  }
};

/**
 * Déconnecte l'utilisateur
 */
export function logout(): void {
  // Supprimez le token et les données utilisateur
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  // Redirigez vers la page de connexion si nécessaire
  window.location.href = '/login';
}

// Exportez toutes les fonctions
export const authService = {
  isAuthenticated,
  getToken,
  getUser,
  login,
  logout,
};

